import { createContext, useContext, useState, useCallback } from 'react';

const translations = {
  en: {
    // Navigation
    nav: {
      home: 'Home',
      about: 'About Us',
      services: 'Services',
      getInTouch: 'Get in Touch',
      contact: 'Contact Us',
    },
    // Hero Section
    hero: {
      title: 'Your project is our responsibility.',
      subtitle: 'At EuroFortaleza, safety is our top priority. We strictly follow UK safety regulations and industry standards to ensure a secure working environment for our team and our clients.',
      cta: 'Request a Quote',
    },
    // Company Overview
    overview: {
      label: 'About EuroFortaleza',
      title: 'Building Excellence Across the UK',
      description: 'With over 5 years of experience delivering exceptional construction and renovation services across England, EuroFortaleza has become synonymous with quality, precision, and professionalism in the UK construction industry.',
      learnMore: 'Learn More About Us',
    },
    // Services
    services: {
      label: 'Our Services',
      title: 'Construction & Renovation Expertise',
      viewAll: 'View All Services',
      construction: {
        title: 'General Construction',
        items: {
          demolition: { title: 'Demolition', desc: 'Professional and safe demolition services for residential and commercial properties.' },
          roofing: { title: 'Roofing', desc: 'Complete roofing solutions including installation, repairs, and maintenance.' },
          extensions: { title: 'Extensions', desc: 'Quality home extensions to expand your living space.' },
          flooring: { title: 'Flooring', desc: 'All types of flooring installation and finishing.' },
          metalStructures: { title: 'Metal Structures', desc: 'Custom metal framework and structural steel work.' },
          plastering: { title: 'Plastering', desc: 'Expert plastering services for smooth, professional finishes.' },
          plasterboard: { title: 'Plasterboard Installation', desc: 'Professional drywall and plasterboard fitting.' },
          electrical: { title: 'Electrical Work', desc: 'Certified electrical installations and repairs.' },
          plumbing: { title: 'Plumbing', desc: 'Complete plumbing services from installation to repairs.' },
          civil: { title: 'Civil Construction', desc: 'Comprehensive civil construction services.' },
        }
      },
      renovation: {
        title: 'General Renovation',
        items: {
          plasterboard: { title: 'Plasterboard', desc: 'Expert plasterboard installation and finishing.' },
          tiling: { title: 'Ceramic Tiling', desc: 'Professional ceramic and porcelain tiling services.' },
          laminate: { title: 'Laminate Flooring', desc: 'Quality laminate flooring installation.' },
          bathroom: { title: 'Bathroom Renovation', desc: 'Complete bathroom transformations from design to finish.' },
          painting: { title: 'Painting', desc: 'Interior and exterior painting with premium finishes.' },
          extensions: { title: 'Extensions', desc: 'Home extensions that blend seamlessly with your property.' },
          loft: { title: 'Loft Conversions', desc: 'Transform unused loft space into functional living areas.' },
        }
      },
      getQuote: 'Get a Quote',
    },
    // UK Map Section
    coverage: {
      label: 'Service Area',
      title: 'Nationwide Coverage',
      description: 'We operate across the entire United Kingdom.',
      subtitle: 'From Scotland to the South Coast, our professional teams deliver exceptional construction and renovation services wherever you are.',
    },
    // Why Choose Us
    whyUs: {
      label: 'Why Choose Us',
      title: 'The EuroFortaleza Difference',
      items: {
        safety: { title: 'Safety Compliance', desc: 'We strictly adhere to UK safety regulations and industry standards.' },
        quality: { title: 'Quality Workmanship', desc: 'Exceptional craftsmanship and attention to detail in every project.' },
        experience: { title: 'Professional Experience', desc: 'Over 5 years of proven expertise in the UK construction market.' },
        reliable: { title: 'Reliable Service', desc: 'Dependable teams committed to completing projects on time.' },
      }
    },
    // About Page
    about: {
      hero: {
        label: 'About EuroFortaleza',
        title: 'Building Trust, Delivering Excellence',
        subtitle: 'Over 5 years of professional construction and renovation services across the United Kingdom.',
      },
      story: {
        title: 'Our Story',
        p1: 'EuroFortaleza has been operating in the UK market for over 5 years, delivering services across England with professionalism and technical expertise.',
        p2: 'Our focus is to provide high-quality workmanship, ensuring strong structures and excellent finishing standards.',
        p3: 'We specialize in general construction, including demolition, roofing, extensions, all types of flooring, metal structures, plastering, plasterboard installation, electrical work, plumbing, and other civil construction services.',
        p4: 'We also carry out full property renovations, including plasterboard, tiling, laminate flooring, bathrooms, painting, extensions, and loft conversions.',
        p5: 'EuroFortaleza is committed to quality, precision, and excellence in every project undertaken.',
      },
      safety: {
        label: 'Safety First',
        title: 'Safety and Compliance in the UK Construction Industry',
        description: 'At EuroFortaleza, safety is not just a requirement—it\'s our core value. We are committed to maintaining the highest safety standards on every project.',
        items: {
          regulations: 'Full compliance with UK Health & Safety regulations',
          training: 'Regular safety training for all team members',
          equipment: 'Use of certified safety equipment and PPE',
          assessments: 'Comprehensive risk assessments before every project',
          insurance: 'Full public liability insurance coverage',
        }
      },
    },
    // Contact Form
    form: {
      title: 'Get in Touch',
      subtitle: 'Have a project in mind? Fill out the form below and our team will get back to you within 24 hours.',
      firstName: 'First Name',
      email: 'Email Address',
      phone: 'Phone Number',
      city: 'City',
      message: 'Your Message',
      submit: 'Send Message',
      sending: 'Sending...',
      success: 'Message sent successfully!',
      successSubtitle: 'Thank you for contacting us. We will get back to you within 24 hours.',
      error: 'Failed to send message. Please try again.',
      validation: {
        firstName: 'First name is required',
        email: 'Valid email is required',
        phone: 'Phone number is required',
        city: 'City is required',
        message: 'Message is required',
      },
      placeholders: {
        firstName: 'Your first name',
        email: 'your@email.com',
        phone: '+44 7XXX XXXXXX',
        city: 'London',
        message: 'Tell us about your project...',
      }
    },
    // Contact Page
    contact: {
      hero: {
        label: 'Contact Us',
        title: 'Let\'s Build Together',
        subtitle: 'Get in touch with our team to discuss your construction or renovation project.',
      },
      info: {
        title: 'Contact Information',
        phone: 'Phone',
        email: 'Email',
        address: 'Address',
        whatsapp: 'WhatsApp',
        instagram: 'Instagram',
        hours: 'Business Hours',
        hoursValue: 'Monday - Saturday: 7:00 AM - 5:00 PM',
      }
    },
    // Footer
    footer: {
      description: 'Professional construction and renovation services across the United Kingdom. Quality workmanship, safety compliance, and exceptional results.',
      quickLinks: 'Quick Links',
      contactInfo: 'Contact Info',
      businessHours: 'Business Hours',
      hours: 'Monday - Saturday',
      hoursTime: '7:00 AM - 5:00 PM',
      sunday: 'Sunday: Closed',
      copyright: '© 2025 EuroFortaleza. All rights reserved.',
      tagline: 'Building Excellence Across the UK',
    },
    // Meta
    meta: {
      home: {
        title: 'EuroFortaleza | Construction & Renovation Services UK',
        description: 'Professional construction and renovation services across the United Kingdom. Demolition, roofing, extensions, loft conversions and more. Quality workmanship guaranteed.',
      },
      about: {
        title: 'About Us | EuroFortaleza Construction UK',
        description: 'Learn about EuroFortaleza - over 5 years of professional construction and renovation expertise in the UK. Safety compliance, quality workmanship, reliable service.',
      },
      services: {
        title: 'Our Services | EuroFortaleza Construction & Renovation UK',
        description: 'Comprehensive construction and renovation services in the UK. Demolition, roofing, extensions, flooring, plumbing, electrical work, loft conversions and more.',
      },
      getInTouch: {
        title: 'Get in Touch | EuroFortaleza UK',
        description: 'Contact EuroFortaleza for a free quote on your construction or renovation project. Professional services across the entire United Kingdom.',
      },
      contact: {
        title: 'Contact Us | EuroFortaleza Construction UK',
        description: 'Get in touch with EuroFortaleza. Phone, email, WhatsApp - we\'re here to help with your construction and renovation needs across the UK.',
      },
    },
  },
  pt: {
    // Navigation
    nav: {
      home: 'Inicio',
      about: 'Sobre Nos',
      services: 'Servicos',
      getInTouch: 'Contato',
      contact: 'Fale Conosco',
    },
    // Hero Section
    hero: {
      title: 'Seu projeto e nossa responsabilidade.',
      subtitle: 'Na EuroFortaleza, a seguranca e nossa prioridade. Seguimos rigorosamente as regulamentacoes de seguranca do Reino Unido e os padroes da industria para garantir um ambiente de trabalho seguro para nossa equipe e nossos clientes.',
      cta: 'Solicitar Orcamento',
    },
    // Company Overview
    overview: {
      label: 'Sobre a EuroFortaleza',
      title: 'Construindo Excelencia em Todo o Reino Unido',
      description: 'Com mais de 5 anos de experiencia entregando servicos excepcionais de construcao e renovacao em toda a Inglaterra, a EuroFortaleza tornou-se sinonimo de qualidade, precisao e profissionalismo na industria da construcao do Reino Unido.',
      learnMore: 'Saiba Mais Sobre Nos',
    },
    // Services
    services: {
      label: 'Nossos Servicos',
      title: 'Especialistas em Construcao e Renovacao',
      viewAll: 'Ver Todos os Servicos',
      construction: {
        title: 'Construcao Geral',
        items: {
          demolition: { title: 'Demolicao', desc: 'Servicos de demolicao profissionais e seguros para propriedades residenciais e comerciais.' },
          roofing: { title: 'Telhados', desc: 'Solucoes completas de telhados incluindo instalacao, reparos e manutencao.' },
          extensions: { title: 'Extensoes', desc: 'Extensoes de qualidade para expandir seu espaco.' },
          flooring: { title: 'Pisos', desc: 'Instalacao e acabamento de todos os tipos de pisos.' },
          metalStructures: { title: 'Estruturas Metalicas', desc: 'Estruturas metalicas personalizadas e trabalho em aco estrutural.' },
          plastering: { title: 'Reboco', desc: 'Servicos especializados de reboco para acabamentos profissionais.' },
          plasterboard: { title: 'Drywall', desc: 'Instalacao profissional de drywall e gesso.' },
          electrical: { title: 'Eletrica', desc: 'Instalacoes e reparos eletricos certificados.' },
          plumbing: { title: 'Encanamento', desc: 'Servicos completos de encanamento.' },
          civil: { title: 'Construcao Civil', desc: 'Servicos abrangentes de construcao civil.' },
        }
      },
      renovation: {
        title: 'Renovacao Geral',
        items: {
          plasterboard: { title: 'Drywall', desc: 'Instalacao e acabamento especializado de drywall.' },
          tiling: { title: 'Azulejos', desc: 'Servicos profissionais de azulejos ceramicos e porcelanato.' },
          laminate: { title: 'Piso Laminado', desc: 'Instalacao de piso laminado de qualidade.' },
          bathroom: { title: 'Renovacao de Banheiros', desc: 'Transformacoes completas de banheiros do projeto ao acabamento.' },
          painting: { title: 'Pintura', desc: 'Pintura interior e exterior com acabamentos premium.' },
          extensions: { title: 'Extensoes', desc: 'Extensoes que se integram perfeitamente a sua propriedade.' },
          loft: { title: 'Conversao de Sotao', desc: 'Transforme espaco de sotao nao utilizado em areas funcionais.' },
        }
      },
      getQuote: 'Solicitar Orcamento',
    },
    // UK Map Section
    coverage: {
      label: 'Area de Servico',
      title: 'Cobertura Nacional',
      description: 'Operamos em todo o Reino Unido.',
      subtitle: 'Da Escocia a costa sul, nossas equipes profissionais entregam servicos excepcionais de construcao e renovacao onde voce estiver.',
    },
    // Why Choose Us
    whyUs: {
      label: 'Por Que Nos Escolher',
      title: 'O Diferencial EuroFortaleza',
      items: {
        safety: { title: 'Conformidade com Seguranca', desc: 'Seguimos rigorosamente as regulamentacoes de seguranca do Reino Unido.' },
        quality: { title: 'Qualidade de Trabalho', desc: 'Excelente artesanato e atencao aos detalhes em cada projeto.' },
        experience: { title: 'Experiencia Profissional', desc: 'Mais de 5 anos de experiencia comprovada no mercado de construcao do Reino Unido.' },
        reliable: { title: 'Servico Confiavel', desc: 'Equipes confiaveis comprometidas em concluir projetos no prazo.' },
      }
    },
    // About Page
    about: {
      hero: {
        label: 'Sobre a EuroFortaleza',
        title: 'Construindo Confianca, Entregando Excelencia',
        subtitle: 'Mais de 5 anos de servicos profissionais de construcao e renovacao em todo o Reino Unido.',
      },
      story: {
        title: 'Nossa Historia',
        p1: 'A EuroFortaleza opera no mercado do Reino Unido ha mais de 5 anos, entregando servicos em toda a Inglaterra com profissionalismo e experiencia tecnica.',
        p2: 'Nosso foco e fornecer trabalho de alta qualidade, garantindo estruturas solidas e excelentes padroes de acabamento.',
        p3: 'Somos especializados em construcao geral, incluindo demolicao, telhados, extensoes, todos os tipos de pisos, estruturas metalicas, reboco, instalacao de drywall, trabalho eletrico, encanamento e outros servicos de construcao civil.',
        p4: 'Tambem realizamos renovacoes completas de propriedades, incluindo drywall, azulejos, piso laminado, banheiros, pintura, extensoes e conversoes de sotao.',
        p5: 'A EuroFortaleza esta comprometida com qualidade, precisao e excelencia em cada projeto realizado.',
      },
      safety: {
        label: 'Seguranca em Primeiro Lugar',
        title: 'Seguranca e Conformidade na Industria da Construcao do Reino Unido',
        description: 'Na EuroFortaleza, seguranca nao e apenas um requisito - e nosso valor central. Estamos comprometidos em manter os mais altos padroes de seguranca em cada projeto.',
        items: {
          regulations: 'Total conformidade com as regulamentacoes de Saude e Seguranca do Reino Unido',
          training: 'Treinamento regular de seguranca para todos os membros da equipe',
          equipment: 'Uso de equipamentos de seguranca e EPIs certificados',
          assessments: 'Avaliacoes de risco abrangentes antes de cada projeto',
          insurance: 'Cobertura completa de seguro de responsabilidade civil',
        }
      },
    },
    // Contact Form
    form: {
      title: 'Entre em Contato',
      subtitle: 'Tem um projeto em mente? Preencha o formulario abaixo e nossa equipe entrara em contato em ate 24 horas.',
      firstName: 'Nome',
      email: 'E-mail',
      phone: 'Telefone',
      city: 'Cidade',
      message: 'Sua Mensagem',
      submit: 'Enviar Mensagem',
      sending: 'Enviando...',
      success: 'Mensagem enviada com sucesso!',
      successSubtitle: 'Obrigado por entrar em contato. Retornaremos em ate 24 horas.',
      error: 'Falha ao enviar mensagem. Tente novamente.',
      validation: {
        firstName: 'Nome e obrigatorio',
        email: 'E-mail valido e obrigatorio',
        phone: 'Telefone e obrigatorio',
        city: 'Cidade e obrigatoria',
        message: 'Mensagem e obrigatoria',
      },
      placeholders: {
        firstName: 'Seu nome',
        email: 'seu@email.com',
        phone: '+44 7XXX XXXXXX',
        city: 'Londres',
        message: 'Conte-nos sobre seu projeto...',
      }
    },
    // Contact Page
    contact: {
      hero: {
        label: 'Fale Conosco',
        title: 'Vamos Construir Juntos',
        subtitle: 'Entre em contato com nossa equipe para discutir seu projeto de construcao ou renovacao.',
      },
      info: {
        title: 'Informacoes de Contato',
        phone: 'Telefone',
        email: 'E-mail',
        address: 'Endereco',
        whatsapp: 'WhatsApp',
        instagram: 'Instagram',
        hours: 'Horario de Funcionamento',
        hoursValue: 'Segunda - Sabado: 7:00 - 17:00',
      }
    },
    // Footer
    footer: {
      description: 'Servicos profissionais de construcao e renovacao em todo o Reino Unido. Qualidade de trabalho, conformidade com seguranca e resultados excepcionais.',
      quickLinks: 'Links Rapidos',
      contactInfo: 'Informacoes de Contato',
      businessHours: 'Horario de Funcionamento',
      hours: 'Segunda - Sabado',
      hoursTime: '7:00 - 17:00',
      sunday: 'Domingo: Fechado',
      copyright: '© 2025 EuroFortaleza. Todos os direitos reservados.',
      tagline: 'Construindo Excelencia em Todo o Reino Unido',
    },
    // Meta
    meta: {
      home: {
        title: 'EuroFortaleza | Servicos de Construcao e Renovacao UK',
        description: 'Servicos profissionais de construcao e renovacao em todo o Reino Unido. Demolicao, telhados, extensoes, conversoes de sotao e mais. Qualidade garantida.',
      },
      about: {
        title: 'Sobre Nos | EuroFortaleza Construcao UK',
        description: 'Conheca a EuroFortaleza - mais de 5 anos de experiencia profissional em construcao e renovacao no Reino Unido.',
      },
      services: {
        title: 'Nossos Servicos | EuroFortaleza Construcao e Renovacao UK',
        description: 'Servicos abrangentes de construcao e renovacao no Reino Unido. Demolicao, telhados, extensoes, pisos, encanamento, eletrica, conversoes de sotao e mais.',
      },
      getInTouch: {
        title: 'Entre em Contato | EuroFortaleza UK',
        description: 'Entre em contato com a EuroFortaleza para um orcamento gratuito do seu projeto de construcao ou renovacao.',
      },
      contact: {
        title: 'Fale Conosco | EuroFortaleza Construcao UK',
        description: 'Entre em contato com a EuroFortaleza. Telefone, e-mail, WhatsApp - estamos aqui para ajudar com suas necessidades de construcao e renovacao.',
      },
    },
  }
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');

  const toggleLanguage = useCallback(() => {
    setLanguage(prev => prev === 'en' ? 'pt' : 'en');
  }, []);

  const t = useCallback((path) => {
    const keys = path.split('.');
    let value = translations[language];
    for (const key of keys) {
      value = value?.[key];
    }
    return value || path;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
